import React from 'react';
import { Stack, Table, TableBody, TableHead, TableRow, TableCell, Collapse } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Currency } from '../constants/currencies';
import {
  FormTextField,
  FormNumberField
} from '../../../../../Components/CustomUIElements/FormTextField';
import FormRadioButtonGroup from '../../../../../Components/CustomUIElements/FormRadioButtonGroup';
import { classes as tableStyles } from '../../../../../../../components/TableStyles';
import { theme } from '../../../../../../ThemeContext/ThemeObject';
import CurrencyInput from './CurrencyInput';

interface AdditionItem {
  name: string;
  type: string;
  value: { value: string; currency: Currency };
}

interface AdditionTableProps {
  items: AdditionItem[];
  onUpdate: (items: AdditionItem[]) => void;
  isReadOnly?: boolean;
}

const headerStyle = (name: string) => {
  switch (name) {
    case 'Name':
      return { width: '220px' };
    case 'Type':
      return { width: '285px' };
    case 'Value':
      return { width: '250px' };
    default:
      return { width: '60px' };
  }
};

export default function AdditionTable({ items, onUpdate, isReadOnly = false }: AdditionTableProps) {
  const handleNameChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index].name = value;
    onUpdate(updatedItems);
  };

  const handleTypeChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index].type = value;
    onUpdate(updatedItems);
  };

  const handleValueChange = (index: number, value: string) => {
    const updatedItems = [...items];
    updatedItems[index].value.value = value;
    onUpdate(updatedItems);
  };

  const handleCurrencyChange = (index: number, value: Currency) => {
    const updatedItems = [...items];
    updatedItems[index].value.currency = value;
    onUpdate(updatedItems);
  };

  const handleDelete = (index: number) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    onUpdate(updatedItems);
  };

  return (
    <Collapse in={!!items.length}>
      <Table stickyHeader sx={{ ...tableStyles.tableStyling, paddingBottom: 2 }}>
        <TableHead>
          <TableRow>
            {['Name', 'Type', 'Value', ''].map((title, index) => (
              <TableCell sx={{ ...tableStyles.tableHeader, ...headerStyle(title) }} key={index}>
                <Stack>{title}</Stack>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <TableRow key={index}>
              <TableCell>
                {isReadOnly ? (
                  item.name
                ) : (
                  <FormTextField
                    value={item.name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                  />
                )}
              </TableCell>
              <TableCell>
                {isReadOnly ? (
                  <Stack>Percentage</Stack>
                ) : (
                  <FormRadioButtonGroup
                    options={[
                      { label: 'Fixed amount', value: 'FixedAmount' },
                      { label: 'Percentage', value: 'MultipleOfBase' }
                    ]}
                    onChange={(value) => handleTypeChange(index, value.toString())}
                    directionRow
                    color={'#939393'}
                    defaultValue={item.type}
                  />
                )}
              </TableCell>
              <TableCell sx={{ div: { margin: 'unset' } }}>
                {!!item.type && (
                  <>
                    {item.type === 'MultipleOfBase' ? (
                      <Stack direction="row" alignItems="center" gap={1}>
                        <FormNumberField
                          value={item.value.value}
                          onChange={(e) => handleValueChange(index, e.target.value)}
                          fullWidth
                          placeholder="e.g. 10"
                        />
                        %
                      </Stack>
                    ) : (
                      <CurrencyInput
                        value={item.value.value}
                        currency={item.value.currency}
                        onChange={(value) => handleValueChange(index, value)}
                        onCurrencyChange={(value) => handleCurrencyChange(index, value)}
                        fullWidth
                      />
                    )}
                  </>
                )}
              </TableCell>
              <TableCell>
                <DeleteIcon
                  onClick={() => handleDelete(index)}
                  sx={{ color: theme.palette.error.main }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Collapse>
  );
}
